// API service for backend communication

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://osin7-backend-355859990695.us-central1.run.app';

const APP_VERSION = process.env.APP_VERSION || '0.3.1dev';

console.log('App Version:', APP_VERSION);

class ApiService {
  /**
   * Pre-search: Get estimated count of results
   * @param {string} selector - Email or phone number
   * @param {string} selectorType - 'email' or 'phone'
   * @returns {Promise<{count: number, selector: string, selector_type: string}>}
   */
  static async presearch(selector, selectorType) {
    const response = await fetch(`${API_BASE_URL}/api/search/presearch`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        selector,
        selector_type: selectorType,
      }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Pre-search failed' }));
      throw new Error(error.message || 'Pre-search failed');
    }

    return response.json();
  }

  /**
   * Create checkout session (creates job + returns Stripe checkout URL)
   * @param {Object} params
   * @param {string} params.tier - 'tier1' or 'tier2'
   * @param {string} params.selector - Email or phone number
   * @param {string} params.selectorType - 'email' or 'phone'
   * @param {string} [params.fullName] - Full name (optional, for tier2)
   * @param {string} [params.company] - Company name (optional, for tier2)
   * @returns {Promise<{sessionId: string, url: string, report_id: string}>}
   */
  static async createCheckoutSession(params) {
    const payload = {
      tier: params.tier,
      selector: params.selector,
      selector_type: params.selectorType,
    };

    // Add optional fields for tier2
    if (params.fullName) {
      payload.full_name = params.fullName;
    }
    if (params.company) {
      payload.company = params.company;
    }

    console.log('Creating checkout session with payload:', payload);
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/payment/create-checkout-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      console.log('Checkout response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Checkout error response:', errorText);
        
        let errorMessage = 'Checkout creation failed';
        try {
          const errorJson = JSON.parse(errorText);
          errorMessage = errorJson.message || errorJson.error || errorMessage;
        } catch (e) {
          errorMessage = errorText || errorMessage;
        }
        
        throw new Error(errorMessage);
      }

      const result = await response.json();
      console.log('Checkout session created:', result);
      return result;
    } catch (error) {
      if (error instanceof TypeError && error.message.includes('fetch')) {
        console.error('Network error - is the backend running?');
        throw new Error('Cannot connect to backend. Is the server running?');
      }
      throw error;
    }
  }

  /**
   * Execute job (requires credits)
   * @param {string} reportId - The report ID
   * @returns {Promise<{status: string, report_id: string, tier: string}>}
   */
  static async executeJob(reportId) {
    const response = await fetch(`${API_BASE_URL}/api/search/execute-job`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        report_id: reportId,
      }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Job execution failed' }));
      throw new Error(error.message || 'Job execution failed');
    }

    return response.json();
  }

  /**
   * Upgrade job from tier1 to tier2
   * @param {string} reportId - The report ID
   * @param {string} [fullName] - Full name (optional)
   * @param {string} [company] - Company name (optional)
   * @returns {Promise<{status: string, report_id: string, tier: string, is_upgrade: boolean}>}
   */
  static async upgradeJob(reportId, fullName, company) {
    const payload = {
      report_id: reportId,
    };

    if (fullName) {
      payload.full_name = fullName;
    }
    if (company) {
      payload.company = company;
    }

    const response = await fetch(`${API_BASE_URL}/api/search/upgrade-job`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Job upgrade failed' }));
      throw new Error(error.message || 'Job upgrade failed');
    }

    return response.json();
  }

  /**
   * Confirm payment and add credits (for local dev without webhooks)
   * @param {Object} params
   * @param {string} [params.session_id] - Stripe session ID from URL
   * @param {string} [params.report_id] - Report ID (optional)
   * @returns {Promise<{status: string, report_id: string}>}
   */
  static async confirmPayment({ session_id, report_id }) {
    console.log('Confirming payment with:', { session_id, report_id });
    
    const response = await fetch(`${API_BASE_URL}/api/payment/confirm-payment`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        session_id,
        report_id,
      }),
    });

    console.log('Confirm payment response status:', response.status);

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Confirm payment error:', errorText);
      
      let errorMessage = 'Failed to confirm payment';
      try {
        const errorJson = JSON.parse(errorText);
        errorMessage = errorJson.message || errorJson.error || errorMessage;
      } catch (e) {
        errorMessage = errorText || errorMessage;
      }
      
      throw new Error(errorMessage);
    }

    const result = await response.json();
    console.log('Payment confirmed:', result);
    return result;
  }

  /**
   * Health check
   * @returns {Promise<{status: string, service: string}>}
   */
  static async healthCheck() {
    const response = await fetch(`${API_BASE_URL}/health`);
    
    if (!response.ok) {
      throw new Error('Health check failed');
    }

    return response.json();
  }
}

export default ApiService;

